/**
 * F-073 — Auth controller.
 *
 * Wraps Supabase Auth with a small surface tailored to the game:
 *   - `getUser()` returns the current session user or null.
 *   - `signInWithGoogle()` triggers OAuth redirect.
 *   - `signOut()` clears the session.
 *   - `subscribe(listener)` broadcasts session changes.
 *
 * Constructor takes an injectable client for testing — production callers
 * use `createAuthController(getSupabaseClient())`.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

export interface AuthUser {
  id: string;
  email?: string;
  displayName: string;
  avatarUrl?: string;
}

export interface AuthController {
  getUser(): AuthUser | null;
  signInWithGoogle(redirectTo?: string): Promise<void>;
  signOut(): Promise<void>;
  subscribe(listener: (user: AuthUser | null) => void): () => void;
  /** True when a real Supabase client is wired (vs offline mode). */
  isAvailable(): boolean;
  /**
   * F-082 — override the displayName seen by listeners. Pass `null` to
   * remove the override (revert to the Google account name). Triggers a
   * fresh broadcast so subscribers (UI) re-render with the new name.
   */
  setNickname(nickname: string | null): void;
}

interface SupabaseUserMeta {
  full_name?: string;
  name?: string;
  avatar_url?: string;
  picture?: string;
  email?: string;
}

function toAuthUser(rawUser: {
  id: string;
  email?: string | null;
  user_metadata?: SupabaseUserMeta | null;
} | null): AuthUser | null {
  if (!rawUser) return null;
  const meta = rawUser.user_metadata ?? {};
  const name = meta.full_name ?? meta.name ?? rawUser.email ?? "Player";
  const avatar = meta.avatar_url ?? meta.picture;
  const out: AuthUser = {
    id: rawUser.id,
    displayName: name,
  };
  if (rawUser.email) out.email = rawUser.email;
  if (avatar) out.avatarUrl = avatar;
  return out;
}

export function createAuthController(
  client: SupabaseClient | null,
): AuthController {
  const listeners = new Set<(u: AuthUser | null) => void>();
  // `rawCurrent` holds the Google-derived user; `nicknameOverride` is the
  // user's chosen display name (set explicitly via setNickname). Public
  // surface (getUser, broadcast) emits a merged view with the override
  // applied to displayName. F-082.
  let rawCurrent: AuthUser | null = null;
  let nicknameOverride: string | null = null;

  const merged = (): AuthUser | null => {
    if (!rawCurrent) return null;
    if (!nicknameOverride) return rawCurrent;
    return { ...rawCurrent, displayName: nicknameOverride };
  };

  const broadcast = (user: AuthUser | null): void => {
    rawCurrent = user;
    if (!user) nicknameOverride = null; // sign-out clears override
    const view = merged();
    for (const l of listeners) l(view);
  };

  if (client) {
    let bootstrapped = false;
    // Initial state — only fire if no live auth event has overtaken us.
    void client.auth.getUser().then(({ data }) => {
      if (bootstrapped) return;
      const u = toAuthUser(data.user as Parameters<typeof toAuthUser>[0]);
      if (u !== null) {
        bootstrapped = true;
        broadcast(u);
      }
    });
    client.auth.onAuthStateChange((event, session) => {
      bootstrapped = true;
      broadcast(
        toAuthUser(
          (session?.user ?? null) as Parameters<typeof toAuthUser>[0],
        ),
      );
      // F-081 — sweep stale OAuth fragment AFTER supabase-js has had its
      // chance to consume the callback hash. Doing it earlier (e.g. at
      // module boot) races with supabase-js's own detectSessionInUrl
      // and silently kills the new session.
      if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
        ensureCleanLocationHash();
      }
    });
  }

  return {
    getUser: () => merged(),
    isAvailable: () => client !== null,
    signInWithGoogle: async (redirectTo) => {
      if (!client) throw new Error("auth_unavailable");
      // F-080 — strip hash + query from the redirectTo. Otherwise a
      // leftover `#access_token=...` from a prior callback gets passed
      // back to Supabase and the next callback returns a doubly-fragmented
      // URL like `#%23access_token=...` which our client cannot parse.
      const cleanedRedirect = normalizeRedirectUrl(redirectTo);
      await client.auth.signInWithOAuth({
        provider: "google",
        options: {
          ...(cleanedRedirect ? { redirectTo: cleanedRedirect } : {}),
          queryParams: { prompt: "select_account" },
        },
      });
    },
    signOut: async () => {
      if (!client) throw new Error("auth_unavailable");
      await client.auth.signOut();
      broadcast(null);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      listener(merged());
      return () => listeners.delete(listener);
    },
    setNickname: (nickname) => {
      const trimmed = nickname?.trim() ?? "";
      const next = trimmed.length > 0 ? trimmed : null;
      if (next === nicknameOverride) return; // idempotent
      nicknameOverride = next;
      const view = merged();
      for (const l of listeners) l(view);
    },
  };
}

/**
 * F-080 — strip hash + query off a redirect URL so OAuth callbacks
 * never accumulate fragment artifacts. Returns `undefined` for invalid
 * input so caller can fall through to Supabase's default site URL.
 */
export function normalizeRedirectUrl(input: string | undefined): string | undefined {
  if (!input) return undefined;
  try {
    const u = new URL(input);
    return `${u.origin}${u.pathname}`;
  } catch {
    return undefined;
  }
}

/**
 * F-080 — call once on bootstrap. If the location fragment looks like
 * a malformed OAuth callback (e.g. `#%23access_token=...` from a doubly-
 * encoded redirect, or stale `#access_token=...` after sign-out), wipe
 * it via history.replaceState so the user can sign in again cleanly.
 *
 * Returns true when a cleanup happened — useful for tests.
 */
export function ensureCleanLocationHash(): boolean {
  if (typeof window === "undefined" || !window.location) return false;
  const hash = window.location.hash;
  if (!hash) return false;
  const isMalformedOAuthFragment =
    hash.startsWith("#%23access_token") ||
    hash.startsWith("#%23refresh_token") ||
    hash.startsWith("#access_token") ||
    hash.startsWith("#refresh_token") ||
    hash.includes("error=access_denied") ||
    hash.includes("error_description");
  if (!isMalformedOAuthFragment) return false;
  try {
    const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", cleanUrl);
    return true;
  } catch {
    return false;
  }
}

export const __testing = { toAuthUser };
